import { h, Component } from 'preact';
import Store from 'store/store';
import { getData, getAllDomains } from 'utils/dynamicData';
import { joinClasses } from 'utils/utils';
import { renderCookieConsentLink } from 'utils/cookies';
import { copy, a11yCopy, pageName } from 'utils/copy';
import { track } from 'utils/tracking';

import PageLink from 'components/pageLink/pageLink';
import ShareIcons from 'components/shareIcons/shareIcons';

import s from 'components/header/header.sss';

const GlobeIcon = (props) => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
		<g clip-path="url(#clip0_9700_346857)">
			<path d="M7.99998 1.33301C9.6675 3.15858 10.6151 5.5277 10.6666 7.99967C10.6151 10.4717 9.6675 12.8408 7.99998 14.6663M7.99998 1.33301C6.33246 3.15858 5.38481 5.5277 5.33331 7.99967C5.38481 10.4717 6.33246 12.8408 7.99998 14.6663M7.99998 1.33301C4.31808 1.33301 1.33331 4.31778 1.33331 7.99967C1.33331 11.6816 4.31808 14.6663 7.99998 14.6663M7.99998 1.33301C11.6819 1.33301 14.6666 4.31778 14.6666 7.99967C14.6666 11.6816 11.6819 14.6663 7.99998 14.6663M1.66666 5.99967H14.3333M1.66665 9.99967H14.3333" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
		</g>
		<defs>
			<clipPath id="clip0_9700_346857">
				<rect width="16" height="16" fill="white" />
			</clipPath>
		</defs>
	</svg>
);

export default class Header extends Component {

	constructor(props) {
		super(props);

		this.state = {
			open: false,
			links: [],
			expandedGroups: new Set(),
			langSwitcherUrl: null
		};

		this.processLink = this.processLink.bind(this);
		this.toggleOpen = this.toggleOpen.bind(this);
		this.onAnyClick = this.onAnyClick.bind(this);
		this.renderLink = this.renderLink.bind(this);
		this.trackHeaderLogoClick = this.trackHeaderLogoClick.bind(this);

		if (typeof window !== 'undefined') {
			this.loadData();
		}
	}

	async loadData() {
		try {
			const data = await getData('navigation');
			const links = (data?.header?.links || []).map(this.processLink).filter(e => e);
			this.setState({ links });

			const altLangRegion = Store.get().appData?.options?.altLangRegion?.toLowerCase();
			if (altLangRegion) {
				const domains = (await getAllDomains())?.domains || [];
				const altLangDomain = domains.find(d => d.region_code === altLangRegion)?.domain_name || null;
				const langSwitcherUrl = altLangDomain ? `https://${altLangDomain}` : null;
				this.setState({ langSwitcherUrl });
			}
		} catch (err) {
			console.error(err);
		}
	}

	processLink(link) {
		if (typeof link === 'string') {
			link = { destination: link };
		}
		if (!link) return;
		if (!link.name) {
			link.name = pageName(link.destination);
		}
		if (link.sublinks) {
			link.sublinks = link.sublinks.map(this.processLink).filter(e => e);
		}
		return link;
	}

	toggleOpen(v) {
		const open = typeof v === 'boolean' ? v : !this.state.open;
		this.setState({ open });
		document.documentElement.classList[open ? 'add' : 'remove']('header-open');
	}

	toggleGroupExpansion(link) {
		const expandedGroups = this.state.expandedGroups;
		if (expandedGroups.has(link)) {
			expandedGroups.delete(link);
		} else {
			expandedGroups.add(link);
		}
		this.setState({ });
	}

	onAnyClick(e) {
		// close on internal navigation
		const link = e.target.closest('a');
		if (!link) return;
		if (!link.target || link.target === '_self') {
			this.toggleOpen(false);
		}

		let href = link.getAttribute('href');
		if (href) {
			let label = link.innerHTML;
			if (link instanceof HTMLElement) {
				label = link.innerText || link.textContent || '';
			}
			if (label) {
				this.trackPersistentButtonClick(label, href);
			}
		}
	}

	trackHeaderLogoClick(e) {
		const link = e.target.closest('a');
		if (!link) return;

		this.trackPersistentButtonClick('landing', link?.href);
	}

	trackPersistentButtonClick(label) {
		track('header', 'header_nav_click', label);
	}

	renderLink(link) {
		if (link.sublinks) {
			// NOTE: should we use the native popover / anchor positioning?
			return (
				<div class={joinClasses(s.navItem, s.expandable, this.state.expandedGroups.has(link) && s.expanded)}>
					<button class={s.name} onClick={() => this.toggleGroupExpansion(link)}>{link.name}</button>
					<nav class={s.popover}>
						{link.sublinks.map(this.renderLink)}
					</nav>
				</div>
			);
		}
		if (link.destination === 'cookies') {
			return renderCookieConsentLink(link.name);
		}
		if (/^https?:\/\//.test(link.destination)) {
			return (
				<a class={s.navItem} href={link.destination} target="_blank" rel="noopener noreferrer">{link.name}</a>
			);
		}
		return (
			<PageLink to={link.destination} class={s.navItem}>{link.name}</PageLink>
		);
	}

	renderLanguageSwitcher(mobile = false) {
		const langSwitcherUrl = this.state.langSwitcherUrl;
		if (!langSwitcherUrl) return null;
		return (
			<a class={joinClasses(s.langSwitcher, mobile ? s.mobile : s.desktop)} href={langSwitcherUrl}>{copy('$HEADER_LANG_SWITCHER')} <GlobeIcon /></a>
		);
	}

	render() {
		return (
			<header class={joinClasses(s.wrapper, this.state.open && s.open)} onClickCapture={this.onAnyClick}>
				<div class={s.main}>
					<button class={s.burger} onClick={this.toggleOpen} />
					<h1>
						<PageLink to="landing" onClick={this.trackHeaderLogoClick}>
							<img src={`${__IMAGE_ROOT__}/logo.svg`} width="79" height="40" alt={a11yCopy('$UNIVERSAL_ALT_TEXT')} fetchpriority="high" />
						</PageLink>
					</h1>
					{this.renderLanguageSwitcher(true)}
				</div>
				<modal class={s.drawer}>
					<nav class={s.nav}>
						<PageLink to="landing" class={s.mobileOnly} onClick={this.trackHeaderLogoClick}>{copy('$HEADER_MENU_HOME')}</PageLink>
						{this.state.links.map(this.renderLink)}
					</nav>
					<ShareIcons class={s.share} type="header" />
					{this.renderLanguageSwitcher(false)}
				</modal>
			</header>
		);
	}

}